<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type { CascaderOption } from 'naive-ui';
import { NCascader } from 'naive-ui';
import type { Size } from 'naive-ui/es/form/src/interface';
import { findAll, getAllProduct } from '@/service/api/base/product-info';
import type { ProductTypeEnum } from '@/enum';
import { DictTypeEnum } from '@/enum';
import type { Api } from '@/typings/api';

export interface ProductCascaderProps {
  /** 产品类型 */
  productType?: ProductTypeEnum;
  /** 是否多选 */
  multiple?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否可清空 */
  clearable?: boolean;
  /** 是否可搜索 */
  filterable?: boolean;
  /** 最大显示标签数量 */
  maxTagCount?: number;
  /** 额外查询条件 */
  filter?: Api.Common.Filter[];
  /** 选择层级： 0-品牌 1-系列 2-型号 */
  level?: number;
  size?: Size;
}
/** 车膜产品级联选择 */
const props = withDefaults(defineProps<ProductCascaderProps>(), {
  productType: undefined,
  multiple: false,
  disabled: false,
  clearable: true,
  filterable: true,
  maxTagCount: 3,
  size: 'medium',
  valueField: 'id',
  level: 2,
  filter: undefined
});
const model = defineModel<null | string | string[]>('value');
const options = ref<CascaderOption[]>([]);

const dictTypeConfigs = ref([
  { dictType: DictTypeEnum.PRODUCT_NAME, mainColumn: 'productName', selectId: '' },
  { dictType: DictTypeEnum.PRODUCT_VERSION, mainColumn: 'productVersion', selectId: '' },
  { dictType: DictTypeEnum.PRODUCT_SPEC, mainColumn: 'productSpec' }
]);

onMounted(async () => {
  // 如果初始有值，则查询出该产品
  if (model.value) {
    const ids = Array.isArray(model.value) ? model.value : [model.value];
    if (ids.length > 0) {
      const { data } = await findAll({
        column: ['id', 'productName.id', 'productVersion.id', 'productSpec.id'],
        filter: [
          {
            column: 'id',
            type: 'andIn',
            value: ids
          }
        ]
      });
      if (data && data.content && data.content.length > 0) {
        const unwatch = watch(
          () => options.value.length,
          () => {
            if (options.value.length > 0) {
              unwatch();
              data.content.forEach(productInfo => {
                loadOptionByProductInfo(productInfo);
              });
            }
          }
        );
      }
    }
  }
});

watch(
  () => props.productType,
  async (val, old) => {
    if (old !== undefined && val !== old) {
      model.value = null;
    }
    options.value.length = 0;
    options.value = await getChildren({ depth: -1 });
  },
  { immediate: true }
);

/** 加载数据 */
async function handleLoad(option: CascaderOption) {
  return new Promise<void>(resolve => {
    getChildren(option).then(res => {
      option.children = res;
      resolve();
    });
  });
}

async function getChildren(option: CascaderOption) {
  const depth = (option as { depth: number }).depth + 1;
  const config = dictTypeConfigs.value[depth];
  const filter = props.filter ? [...props.filter] : [];
  if (option.value) {
    dictTypeConfigs.value[depth - 1].selectId = option.dictId as string;
    for (let i = 0; i < depth; i += 1) {
      const dictConfig = dictTypeConfigs.value[i];
      filter.push({
        column: `${dictConfig.mainColumn}.id`,
        type: 'andEq',
        value: dictConfig.selectId
      });
    }
  }
  const { data } = await getAllProduct({
    type: props.productType,
    dictType: config.dictType,
    filter
  });
  return data.map((row: any) => {
    return {
      label: row.label,
      value: depth < 2 ? row.id : row.productInfoId,
      dictId: row.id,
      depth,
      isLeaf: depth === props.level
    };
  });
}
/**
 * 根据产品信息加载子节点
 *
 * @param productInfo
 */
function loadOptionByProductInfo(productInfo: Api.Base.ProductInfo) {
  options.value.forEach(async option => {
    if (option.value === productInfo.productName.id) {
      await handleLoad(option);
      option.children?.forEach(child => {
        if (child.value === productInfo.productVersion.id) {
          handleLoad(child);
        }
      });
    }
  });
}
</script>

<template>
  <NCascader
    v-model:value="model"
    :multiple="multiple"
    :options="options"
    placeholder="请选择"
    :disabled="disabled"
    :clearable="clearable"
    :filterable="filterable"
    :max-tag-count="maxTagCount"
    cascade
    show-path
    check-strategy="child"
    :size="size"
    remote
    :on-load="handleLoad"
  ></NCascader>
</template>

<style scoped></style>
